import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  MapPinIcon, 
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Indian Metro Cities
const popularCities = [
  { name: 'Mumbai', icon: '🏛️' },
  { name: 'Delhi NCR', icon: '🏛️' },
  { name: 'Bengaluru', icon: '🏢' },
  { name: 'Hyderabad', icon: '🕌' },
  { name: 'Chennai', icon: '🛕' },
  { name: 'Kolkata', icon: '🏛️' },
  { name: 'Pune', icon: '🏰' },
  { name: 'Ahmedabad', icon: '🏛️' },
  { name: 'Jaipur', icon: '🏰' },
  { name: 'Surat', icon: '🏗️' },
  { name: 'Lucknow', icon: '🏛️' },
  { name: 'Kanpur', icon: '🏗️' }
];

// All Indian Metro and Major Cities
const allCities = [
  'Agra', 'Ahmedabad', 'Ahmednagar', 'Ajmer', 'Akola', 'Alappuzha', 'Aligarh',
  'Allahabad', 'Amravati', 'Amritsar', 'Aurangabad', 'Bangalore', 'Bengaluru',
  'Bhopal', 'Bhubaneswar', 'Chandigarh', 'Chennai', 'Coimbatore', 'Cuttack',
  'Dehradun', 'Delhi', 'Dhanbad', 'Durgapur', 'Ernakulam', 'Faridabad',
  'Gandhinagar', 'Ghaziabad', 'Gorakhpur', 'Gurgaon', 'Gurugram', 'Guwahati',
  'Gwalior', 'Howrah', 'Hubli', 'Hyderabad', 'Indore', 'Jabalpur', 'Jaipur',
  'Jalandhar', 'Jammu', 'Jamshedpur', 'Jodhpur', 'Kakinada', 'Kalyan', 'Kanpur',
  'Kochi', 'Kolhapur', 'Kolkata', 'Kota', 'Kozhikode', 'Lucknow', 'Ludhiana',
  'Madurai', 'Mangalore', 'Meerut', 'Mumbai', 'Mysore', 'Nagpur', 'Nashik',
  'Navi Mumbai', 'Noida', 'Patna', 'Pimpri-Chinchwad', 'Pune', 'Raipur',
  'Rajkot', 'Ranchi', 'Srinagar', 'Surat', 'Thane', 'Thiruvananthapuram',
  'Tiruchirappalli', 'Udaipur', 'Vadodara', 'Varanasi', 'Vasai-Virar',
  'Vijayawada', 'Visakhapatnam', 'Warangal'
].sort();

const SelectLocation = () => {
  const navigate = useNavigate();
  const { user, refreshUser, isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('A');
  const [locationLoading, setLocationLoading] = useState(false);
  const [useCurrentLocationLoading, setUseCurrentLocationLoading] = useState(false);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setUseCurrentLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const response = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            {
              headers: {
                'User-Agent': 'LOCU App'
              }
            }
          );

          const data = response.data;
          if (data) {
            const address = data.address || {};
            const addressParts = [
              address.suburb || address.neighbourhood,
              address.city || address.town || address.village,
              address.state,
              address.postcode
            ].filter(Boolean);

            const locationAddress = addressParts.join(', ') || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

            // Store location in localStorage for non-authenticated users
            localStorage.setItem('selectedLocation', JSON.stringify({
              address: locationAddress,
              latitude,
              longitude,
              timestamp: Date.now()
            }));

            // Update user location in backend if authenticated
            if (isAuthenticated) {
              try {
                await axios.post(`${API_URL}/neighborhood/location`, {
                  latitude,
                  longitude,
                  enabled: true,
                  address: locationAddress
                });
                await refreshUser();
              } catch (error) {
                console.error('Error updating location:', error);
              }
            }
            
            navigate(-1); // Go back to previous page
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
        } finally {
          setUseCurrentLocationLoading(false);
        }
      },
      (error) => {
        console.error('Location access denied:', error);
        alert('Location access denied. Please enable location permissions.');
        setUseCurrentLocationLoading(false);
      }
    );
  };

  const handleCitySelect = async (cityName) => {
    // Store location in localStorage for non-authenticated users
    localStorage.setItem('selectedLocation', JSON.stringify({
      address: cityName,
      timestamp: Date.now()
    }));

    // Update user location in backend if authenticated
    if (isAuthenticated) {
      try {
        await axios.post(`${API_URL}/neighborhood/location`, {
          address: cityName,
          enabled: true
        });
        await refreshUser();
      } catch (error) {
        console.error('Error updating location:', error);
      }
    }
    
    navigate(-1); // Go back to previous page
  };

  const filteredCities = allCities.filter(city =>
    city.toLowerCase().startsWith(selectedLetter.toLowerCase()) &&
    city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group filtered cities into 3 columns
  const columns = [[], [], []];
  filteredCities.forEach((city, index) => {
    columns[index % 3].push(city);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Select Location</h2>
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Bar */}
          <div className="relative mb-6">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search city, area or locality"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>

          {/* Use Current Location */}
          <button
            onClick={getCurrentLocation}
            disabled={useCurrentLocationLoading}
            className="w-full flex items-center gap-3 p-4 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors mb-6 disabled:opacity-50"
          >
            {useCurrentLocationLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            ) : (
              <MapPinIcon className="h-5 w-5 text-primary-600" />
            )}
            <span className="font-semibold text-gray-900">Use Current Location</span>
          </button>

          {/* Popular Cities */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Popular Cities</h3>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-4">
              {popularCities.map((city) => (
                <button
                  key={city.name}
                  onClick={() => handleCitySelect(city.name)}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-primary-200 rounded-xl hover:border-primary-400 hover:bg-primary-50 transition-all"
                >
                  <span className="text-3xl">{city.icon}</span>
                  <span className="text-xs font-medium text-gray-700 text-center">{city.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* All Cities */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">All Cities</h3>
            
            {/* Alphabet Filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  onClick={() => {
                    setSelectedLetter(letter);
                    setSearchTerm('');
                  }}
                  className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                    selectedLetter === letter
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>

            {/* City List in 3 Columns */}
            {filteredCities.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {columns.map((column, colIndex) => (
                  <div key={colIndex} className="space-y-1">
                    {column.map((city) => (
                      <button
                        key={city}
                        onClick={() => handleCitySelect(city)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No cities found starting with "{selectedLetter}"
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectLocation;
